import React from "react";
import { DsButton, DsCard, DsIcon } from "../../../design-system";
import {
  AlertTriangle,
  Info,
  CheckCircle2,
  Bell,
} from "lucide-react";
import { formatInrCompact, formatInrFull } from "./payrollDashboardAnalytics";
import {
  DashCombo,
  DashDonut,
  DashGroupedBars,
  DashHBar,
  DashLine,
  CHART_HEX,
} from "./payrollDashboardCharts";

function AlertList({ alerts = [] }) {
  if (!alerts.length) {
    return (
      <div className="rounded-lg border border-[hsl(var(--ds-border))] px-3 py-4 text-center text-xs text-[hsl(var(--ds-muted-fg))]">
        No payroll alerts for this period.
      </div>
    );
  }
  const iconFor = (tone) =>
    tone === "danger" || tone === "warning"
      ? AlertTriangle
      : tone === "success"
        ? CheckCircle2
        : Info;
  const toneCls = (tone) =>
    tone === "danger"
      ? "border-[hsl(var(--ds-danger)/0.35)] bg-[hsl(var(--ds-danger)/0.08)]"
      : tone === "warning"
        ? "border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.1)]"
        : tone === "success"
          ? "border-[hsl(var(--ds-success)/0.35)] bg-[hsl(var(--ds-success)/0.08)]"
          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.25)]";

  return (
    <div className="space-y-2">
      {alerts.map((a) => (
        <div
          key={a.id}
          className={`flex gap-2 rounded-lg border px-3 py-2 ${toneCls(a.tone)}`}
        >
          <DsIcon icon={iconFor(a.tone)} size="sm" className="mt-0.5 shrink-0 opacity-80" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold">{a.title}</div>
            <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">{a.detail}</div>
          </div>
          {a.action ? (
            <DsButton
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0 self-center"
              onClick={a.action.onClick}
            >
              {a.action.label}
            </DsButton>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mb-2 ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
      {children}
    </div>
  );
}

/** Alerts tab + shared notify strip */
export function DashboardAlertsPanel({ alerts = [] }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <DsIcon icon={Bell} size="sm" className="opacity-70" />
        <SectionTitle>Payroll notifications</SectionTitle>
      </div>
      <AlertList alerts={alerts} />
    </div>
  );
}

export function DashboardMovementsTab({ analytics }) {
  const rev = analytics?.revisions || {};
  const st = analytics?.statutoryChanges || {};
  const flagPie = Object.entries(st.byFlag || {}).map(([k, v]) => ({
    key: k,
    label: k.toUpperCase(),
    value: Number(v) || 0,
  }));
  const revBars = (rev.byMonth || []).map((r) => ({
    label: r.payMonth,
    value: r.count,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Revisions</div>
          <div className="text-lg font-bold">{rev.count || 0}</div>
        </DsCard>
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">CTC delta</div>
          <div className="text-lg font-bold tabular-nums">{formatInrCompact(rev.ctcDelta)}</div>
        </DsCard>
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">With booking / arrears</div>
          <div className="text-lg font-bold">{rev.withArrearsBooking || 0}</div>
        </DsCard>
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Statutory changes</div>
          <div className="text-lg font-bold">{st.count || 0}</div>
        </DsCard>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <DsCard padding="sm" className="!p-3">
          <SectionTitle>Revisions by month (count)</SectionTitle>
          <DashHBar data={revBars} valueFormat="count" />
          {(rev.byMonth || []).length > 1 ? (
            <div className="mt-3">
              <SectionTitle>CTC delta trend</SectionTitle>
              <DashLine
                labels={(rev.byMonth || []).map((r) => r.payMonth)}
                values={(rev.byMonth || []).map((r) => Math.abs(r.ctcDelta || 0))}
              />
            </div>
          ) : null}
        </DsCard>
        <DsCard padding="sm" className="!p-3">
          <SectionTitle>Statutory change rows · flag mix</SectionTitle>
          <DashDonut data={flagPie} />
          <div className="mt-3">
            <DashHBar
              data={(st.byMonth || []).map((r) => ({ label: r.payMonth, value: r.count }))}
              valueFormat="count"
            />
          </div>
        </DsCard>
      </div>
      {analytics?.registerExtras?.revisionArrears > 0 ? (
        <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
          Register arrears in period: {formatInrFull(analytics.registerExtras.revisionArrears)}
        </div>
      ) : null}
    </div>
  );
}

export function DashboardAttendanceTab({ analytics }) {
  const rows = analytics?.attendanceCompare || [];
  const hasAny =
    rows.length > 0 &&
    rows.some(
      (r) =>
        Number(r.attendanceScope) > 0 ||
        Number(r.attendanceProcessed) > 0 ||
        Number(r.payrollHeadcount) > 0
    );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Months</div>
          <div className="text-lg font-bold">{rows.length}</div>
        </DsCard>
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Att scope (sum)</div>
          <div className="text-lg font-bold">
            {rows.reduce((s, r) => s + (Number(r.attendanceScope) || 0), 0)}
          </div>
        </DsCard>
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Att processed (sum)</div>
          <div className="text-lg font-bold">
            {rows.reduce((s, r) => s + (Number(r.attendanceProcessed) || 0), 0)}
          </div>
        </DsCard>
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Att open (sum)</div>
          <div className="text-lg font-bold">
            {rows.reduce((s, r) => s + (Number(r.attendanceUnprocessed) || 0), 0)}
          </div>
        </DsCard>
      </div>
      <DsCard padding="sm" className="!p-3">
        <SectionTitle>Attendance processed vs payroll headcount</SectionTitle>
        {hasAny ? (
          <DashGroupedBars
            labels={rows.map((r) => r.payMonth)}
            series={[
              {
                name: "Att. processed",
                values: rows.map((r) => r.attendanceProcessed),
                color: CHART_HEX[0],
              },
              {
                name: "Att. scope",
                values: rows.map((r) => r.attendanceScope),
                color: CHART_HEX[1],
              },
              {
                name: "Payroll HC",
                values: rows.map((r) => r.payrollHeadcount),
                color: CHART_HEX[2],
              },
            ]}
          />
        ) : (
          <div className="py-6 text-center text-[11px] text-[hsl(var(--ds-muted-fg))]">
            No attendance process counts for these months yet (Process Attendance may not
            have been run). Table below still lists payroll status when available.
          </div>
        )}
        <p className="mt-2 text-[10px] text-[hsl(var(--ds-muted-fg))]">
          Read-only compare from existing attendance process status. Payable days are not
          recalculated here.
        </p>
      </DsCard>
      <DsCard padding="sm" className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ds-table w-full text-xs">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left">Month</th>
                <th className="px-3 py-2 text-right">Att scope</th>
                <th className="px-3 py-2 text-right">Att processed</th>
                <th className="px-3 py-2 text-right">Att open</th>
                <th className="px-3 py-2 text-left">Att locked</th>
                <th className="px-3 py-2 text-right">Payroll HC</th>
                <th className="px-3 py-2 text-left">Payroll</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-[hsl(var(--ds-muted-fg))]">
                    No months in range.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.payMonth} className="border-t border-[hsl(var(--ds-border))]">
                    <td className="px-3 py-1.5 font-medium">{r.payMonth}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.attendanceScope}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.attendanceProcessed}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.attendanceUnprocessed}</td>
                    <td className="px-3 py-1.5">{r.attendanceMonthLocked ? "Yes" : "No"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.payrollHeadcount}</td>
                    <td className="px-3 py-1.5">{r.payrollStatus}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DsCard>
    </div>
  );
}

export function DashboardDeductionsTab({ analytics }) {
  const loans = analytics?.loans || {};
  const enc = analytics?.leaveEncashments || {};
  const loanPie = Object.entries(loans.byStatus || {}).map(([k, v]) => ({
    label: k,
    value: Number(v) || 0,
  }));
  const encPie = Object.entries(enc.byStatus || {}).map(([k, v]) => ({
    label: k,
    value: Number(v) || 0,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Loan apps</div>
          <div className="text-lg font-bold">{loans.total || 0}</div>
        </DsCard>
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Loans pending</div>
          <div className="text-lg font-bold">{loans.pendingApproval || 0}</div>
        </DsCard>
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Encashment req</div>
          <div className="text-lg font-bold">{enc.total || 0}</div>
        </DsCard>
        <DsCard padding="sm" className="!p-2.5">
          <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">Encash days</div>
          <div className="text-lg font-bold">{enc.days || 0}</div>
        </DsCard>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <DsCard padding="sm" className="!p-3">
          <SectionTitle>Loan applications by status</SectionTitle>
          <DashDonut data={loanPie} />
          <div className="mt-2 text-[10px] text-[hsl(var(--ds-muted-fg))]">
            Requested amount (open statuses): {formatInrCompact(loans.amountRequested)}
          </div>
          {analytics?.registerExtras?.loanDeductions > 0 ? (
            <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
              Loan-named deductions in register:{" "}
              {formatInrCompact(analytics.registerExtras.loanDeductions)}
            </div>
          ) : null}
        </DsCard>
        <DsCard padding="sm" className="!p-3">
          <SectionTitle>Leave encashment requests</SectionTitle>
          <DashDonut data={encPie} />
          <div className="mt-3">
            <DashCombo
              labels={(enc.byMonth || []).map((r) => r.payMonth)}
              barValues={(enc.byMonth || []).map((r) => r.count)}
              lineValues={(enc.byMonth || []).map((r) => r.days)}
              barLabel="Requests"
              lineLabel="Days"
            />
          </div>
        </DsCard>
      </div>
    </div>
  );
}

export function DashboardFnfTab({ analytics }) {
  const fnf = analytics?.fnf || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
        {[
          ["F&F count", fnf.count],
          ["Drafts", fnf.draftCount],
          ["Net total", formatInrCompact(fnf.netTotal)],
          ["Leave encash", formatInrCompact(fnf.leaveEncashment)],
          ["Gratuity", formatInrCompact(fnf.gratuity)],
          ["Notice pay", formatInrCompact(fnf.noticePay)],
        ].map(([label, val]) => (
          <DsCard key={label} padding="sm" className="!p-2.5">
            <div className="text-[10px] uppercase text-[hsl(var(--ds-muted-fg))]">{label}</div>
            <div className="text-base font-bold tabular-nums">{val ?? 0}</div>
          </DsCard>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <DsCard padding="sm" className="!p-3">
          <SectionTitle>F&F component mix</SectionTitle>
          <DashDonut data={fnf.mixPie || []} />
          <div className="mt-2 text-[10px] text-[hsl(var(--ds-muted-fg))]">
            Notice recovery: {formatInrCompact(fnf.noticeRecovery)}
          </div>
        </DsCard>
        <DsCard padding="sm" className="!p-3">
          <SectionTitle>F&F net by month</SectionTitle>
          <DashLine
            labels={(fnf.byMonth || []).map((r) => r.payMonth)}
            values={(fnf.byMonth || []).map((r) => r.net)}
          />
          <div className="mt-3">
            <DashGroupedBars
              labels={(fnf.byMonth || []).map((r) => r.payMonth)}
              series={[
                {
                  name: "Notice pay",
                  values: (fnf.byMonth || []).map((r) => r.noticePay),
                  color: CHART_HEX[2],
                },
                {
                  name: "Notice recovery",
                  values: (fnf.byMonth || []).map((r) => r.noticeRecovery),
                  color: CHART_HEX[4],
                },
              ]}
            />
          </div>
        </DsCard>
      </div>
    </div>
  );
}

export function DashboardLiabilityTab({ analytics }) {
  const liab = analytics?.liability || {};
  const pie = [
    { key: "prov", label: "Gratuity provision", value: liab.gratuityProvisionMonthly || 0 },
    { key: "fnf_g", label: "F&F gratuity", value: liab.fnfGratuity || 0 },
    { key: "fnf_le", label: "F&F leave encash", value: liab.fnfLeaveEncashment || 0 },
    { key: "np", label: "Notice pay", value: liab.fnfNoticePay || 0 },
    { key: "nr", label: "Notice recovery", value: liab.fnfNoticeRecovery || 0 },
    { key: "arr", label: "Revision arrears", value: liab.revisionArrears || 0 },
  ].filter((x) => x.value > 0);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.25)] px-3 py-2 text-[10px] text-[hsl(var(--ds-muted-fg))]">
        {liab.note ||
          "Shows existing provision / F&F amounts only — not a full actuarial liability valuation."}
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <DsCard padding="sm" className="!p-3">
          <SectionTitle>Liability-related mix</SectionTitle>
          <DashDonut data={pie} />
        </DsCard>
        <DsCard padding="sm" className="!p-3">
          <SectionTitle>Amounts</SectionTitle>
          <DashHBar
            data={[
              { label: "Gratuity provision", value: liab.gratuityProvisionMonthly || 0 },
              { label: "F&F gratuity", value: liab.fnfGratuity || 0 },
              { label: "F&F leave encash", value: liab.fnfLeaveEncashment || 0 },
              { label: "Notice pay", value: liab.fnfNoticePay || 0 },
              { label: "Notice recovery", value: liab.fnfNoticeRecovery || 0 },
              { label: "Revision arrears", value: liab.revisionArrears || 0 },
              { label: "Loan ded. (register)", value: liab.loanDeductionsInRegister || 0 },
            ]}
          />
        </DsCard>
      </div>
    </div>
  );
}
